import { Fragment, type ReactNode } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { Link } from 'react-router-dom';

export interface Crumb {
  label: string;
  /** Ruta a la que navega. Si se omite (o es la última miga) se renderiza como texto. */
  to?: string;
}

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    flexWrap: 'wrap',
    padding: '11px var(--space-xxl)',
    fontSize: '12.5px',
    color: 'var(--text-tertiary)',
    background: 'var(--bg-deep)',
    borderBottom: '1px solid var(--border-subtle)',
    // Queda fijo bajo el topbar al scrollear (en vistas con scroll de página).
    position: 'sticky',
    top: 'var(--topbar-h)',
    zIndex: 50,
  },
  link: {
    color: 'var(--text-tertiary)',
    textDecoration: 'none',
    transitionProperty: 'color',
    transitionDuration: '150ms',
    ':hover': { color: 'var(--accent)' },
  },
  here: {
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  sep: { opacity: 0.5 },
  // Acciones de la pantalla alineadas a la derecha, en la misma fila de las migas.
  derecha: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
});

/** Migas de pan compartidas — barra full-width bajo el topbar. `derecha` = acciones de la pantalla en la misma fila. */
export function Breadcrumbs({ items, derecha }: { items: Crumb[]; derecha?: ReactNode }) {
  const styles = useStyles();
  return (
    <nav className={styles.bar} aria-label="Migas de pan">
      {items.map((it, i) => {
        const last = i === items.length - 1;
        return (
          <Fragment key={`${it.label}-${i}`}>
            {i > 0 && <span className={styles.sep}>/</span>}
            {last || !it.to ? (
              <span className={styles.here} aria-current={last ? 'page' : undefined}>
                {it.label}
              </span>
            ) : (
              <Link to={it.to} className={styles.link}>
                {it.label}
              </Link>
            )}
          </Fragment>
        );
      })}
      {derecha && <div className={styles.derecha}>{derecha}</div>}
    </nav>
  );
}
